/**
 * 机构后台 · 教学接口:班级与学员(/v1/admin/org)、教学(/v1/admin/learning:班级闯关 / 学员进度 / 批改 / 预警 / 分配课程)、
 * 课程列表(/v1/content/courses,给「分配课程」选)。教师只看得到自己的班与学员(后端按角色裁)。
 */
import { fmtDateTime } from '../ui/format.js';
import type { ApiClient } from './client.js';
import type { RubricBrief } from './courseForms.js';

export interface ClassTeacher {
  id: string;
  name: string;
  phone?: string;
}
export interface ClassRow {
  id: string;
  name: string;
  grade: string | null;
  tags: string[] | null;
  courseIds: string[];
  status: string;
  memberCount: number;
  teachers: ClassTeacher[];
  createdAt: string;
}
export interface StudentRow {
  id: string;
  name: string;
  nickname: string;
  avatar: string;
  birthMonth: string;
  ageBand: string;
  schoolStage: string;
  grade: string | null;
  school: string | null;
  status: string;
  lessonHours: number;
  consentState: string;
  guardianPhone: string | null;
  attribution: 'own' | 'other' | 'none';
  /** 人脸底片:none 未采集 · captured 已采集 · pending_update 待重新采集;consent = 监护人授权过人脸采集 */
  face?: { template: 'none' | 'captured' | 'pending_update'; at: string | null; consent: boolean };
  createdAt: string;
  classes: { id: string; name: string }[];
  guardians: number;
}
export interface StudentView extends Omit<StudentRow, 'guardians'> {
  guardians: { name: string; phone: string; relation: string; primary: boolean }[];
}
export interface LedgerRow {
  id: string;
  studentId?: string;
  student?: { name: string; nickname: string };
  delta: number | string;
  balanceAfter: number | string;
  reason: string;
  note: string | null;
  createdAt: string;
}
export interface CourseOverview {
  courseId: string;
  courseName: string;
  /** 低龄图标积木课(二期 ScratchJr):逐关可以看孩子拼的积木 */
  iconBlocks?: boolean;
  entitlement: { id: string; source: string; status: string; expiresAt: string | null; mine: boolean };
  passed: number;
  current: { levelId: string; seq: number | null; name: string; status: string; remedyCount: number } | null;
  lastActivityAt: string | null;
}
export interface ProgressRow {
  id: string;
  levelId: string;
  status: string;
  remedyCount: number;
  /** 放回学习后重新数的补强次数 */
  remedyRound?: number;
  redoOpen?: number;
  unlockSource?: string | null;
  lastActivityAt: string | null;
  firstPass: { score: number; grade: string; at: string; source?: 'teacher'; reason?: string } | null;
  level: { seq: number; name: string } | null;
}
/** 学员的阶段测评(在用的 + 故障重考作废的) */
export interface StudentAssessment {
  id: string;
  tenantId: string;
  partId: string;
  partName: string;
  kind: string;
  status: 'in_progress' | 'submitted' | 'graded';
  score: number | null;
  maxScore: number;
  passed: boolean | null;
  startedAt: string;
  submittedAt: string | null;
  submitMode: string | null;
  resetReason: string | null;
  resetAt: string | null;
}
/** 数字人待答(超出本关的问题) */
export interface DhQuestion {
  id: string;
  student: { id: string; name: string; nickname: string };
  level: { id: string; seq: number | null; name: string };
  question: string;
  answer: string;
  createdAt: string;
  reply: string | null;
  handledBy: string | null;
  handledAt: string | null;
}
export interface ClassProgressRow {
  student: { id: string; name: string; nickname: string; lessonHours: number };
  passed: number;
  total: number;
  current: { levelId: string; seq: number | null; status: string; remedyCount: number } | null;
  averageScore: number | null;
  lastActivityAt: string | null;
}
export interface ReviewItem {
  id: string;
  kind: string;
  status: string;
  student: { id: string; name: string; nickname: string };
  level: { id: string; seq: number | null; name: string } | null;
  question: {
    id: string;
    type: string;
    stem: string;
    maxScore: number;
    answerKey: unknown;
    explanation: string | null;
  };
  answer: unknown;
  /** dims = 挂了评分表的作品:AI 给的各项建议分(二期) */
  aiSuggestion: { score?: number; comment?: string; dims?: Record<string, number> } | null;
  /** 这道题挂了评分表(二期作品评审):按表逐项打分,合计折成题目分值 */
  rubric?: RubricBrief | null;
  /** 以课程作品评审人身份看到的(不是自己班的学员,姓名已打码;不能改判任务清单) */
  expert?: boolean;
  /** 这一关有随堂实操任务清单:任课教师批作品时可以一起看、改判 */
  hasTasks?: boolean;
  /** 配了 AI 服务:可以点「生成 AI 建议分」 */
  aiAvailable?: boolean;
  /** 只有作业能退回重做(阶段测评、补强复测直接给分) */
  canReturn?: boolean;
  submittedAt: string;
  overdue: boolean;
  hoursLeft: number;
}
export interface AlertRow {
  id: string;
  kind: string;
  status: string;
  payload: Record<string, unknown> | null;
  createdAt: string;
  handledAt?: string | null;
  student: { id: string; name: string; nickname: string };
}
export interface CourseLite {
  id: string;
  name: string;
  status: string;
  track: string;
  origin: string;
}
export interface ImportRow {
  phone: string;
  name: string;
  nickname?: string;
  birthMonth: string;
  schoolStage: string;
  grade?: string;
  school?: string;
}
export interface ImportError {
  row: number;
  field: string;
  message: string;
}
export interface Paged<T> {
  total: number;
  page: number;
  size: number;
  items: T[];
}

export const PROGRESS_LABELS: Record<string, string> = {
  locked: '未解锁',
  unlocked: '可学',
  learning: '学习中',
  pending_grading: '待批改',
  passed: '已通关',
  failed: '未达标',
  remedying: '补强中',
  teacher_intervene: '需教师介入',
};
export const PROGRESS_TONE: Record<string, string> = {
  passed: 'ok',
  learning: 'brand',
  pending_grading: 'warn',
  failed: 'bad',
  remedying: 'amber',
  teacher_intervene: 'bad',
};
export const ALERT_LABELS: Record<string, string> = {
  no_study_7d: '7 天没有学习',
  review_overdue: '作业超 48 小时未批',
  hours_negative: '课时已欠费',
  level_failed_3x: '同一关 3 次未过',
  dh_out_of_scope: '数字人问答超出本关',
};
/** 预警的一句话详情(工作台与批改页共用) */
export function alertDetail(a: { kind: string; payload: Record<string, unknown> | null }): string {
  const p = a.payload ?? {};
  const at = (v: unknown) => fmtDateTime(String(v));
  if (a.kind === 'no_study_7d')
    return p['lastActivityAt'] ? `上次学习 ${at(p['lastActivityAt'])}` : '入学后还没学过';
  const level = p['levelName']
    ? `${p['levelSeq'] !== undefined && p['levelSeq'] !== null ? `第 ${String(p['levelSeq'])} 关「` : '「'}${String(p['levelName'])}」`
    : '';
  if (a.kind === 'review_overdue')
    return [level, p['submittedAt'] ? `${at(p['submittedAt'])} 提交` : ''].filter(Boolean).join(' · ');
  if (a.kind === 'hours_negative')
    return [
      p['balance'] !== undefined ? `余额 ${String(p['balance'])} 课时` : '',
      level ? `通关 ${level}` : '',
    ]
      .filter(Boolean)
      .join(' · ');
  if (a.kind === 'level_failed_3x')
    return [level, p['times'] ? `补强 ${String(p['times'])} 次没过` : ''].filter(Boolean).join(' · ');
  return '';
}
export const SUBMISSION_KIND_LABELS: Record<string, string> = {
  homework: '作业',
  retest: '补强复测',
  assessment: '阶段测评',
  drill: '刷题',
};
export const SOURCE_LABELS: Record<string, string> = {
  org_assign: '机构分配',
  purchase: '购买',
  activation: '激活码',
  school_free: '学校免费专区',
  trial: '体验课',
};
export const STAGE_LABELS: Record<string, string> = {
  primary: '小学',
  junior: '初中',
  senior: '高中',
  adult: '成人',
};
export const RELATION_LABELS: Record<string, string> = {
  account: '登录账号(监护人手机)',
  father: '父亲',
  mother: '母亲',
  other: '其他监护人',
};

/** 工作台(原型 admin/dashboard):管理员看全机构,教师看自己的班 */
export interface Workbench {
  scope: 'tenant' | 'classes';
  now: string;
  tenant: {
    name: string;
    planLabel: string | null;
    /** 这类机构能用的模块(规格 2.4.2):未开通的「解锁」去品牌与模块在线自助开通 */
    modules: {
      key: string;
      label: string;
      on: boolean;
      expired: boolean;
      phase: 'p1' | 'phase2';
      priceNote: string;
    }[];
  } | null;
  students: { total: number; newThisMonth: number };
  active: { week: number; prevWeek: number; rate: number | null };
  passes: { week: number; prevWeek: number };
  lessonHours: { week: number };
  reviews: {
    total: number;
    dueSoon: number;
    overdue: number;
    groups: {
      level: string;
      question: string;
      className: string;
      count: number;
      hoursLeft: number;
      overdue: boolean;
    }[];
  };
  alerts: {
    total: number;
    items: {
      id: string;
      kind: string;
      payload: Record<string, unknown> | null;
      createdAt: string;
      student: { id: string; name: string; nickname: string };
      className: string;
    }[];
  };
  classes: {
    id: string;
    name: string;
    grade: string | null;
    members: number;
    teachers: string[];
    course: string | null;
    totalLevels: number;
    avgPassed: number;
    avgScore: number | null;
    weekPassRate: number | null;
    classOnly: boolean;
  }[];
}

export function teachingApi(api: ApiClient) {
  const o = '/v1/admin/org';
  const l = '/v1/admin/learning';
  return {
    workbench: () => api.get<Workbench>(`${l}/workbench`),
    classes: {
      list: () => api.get<ClassRow[]>(`${o}/classes`),
      create: (body: { name: string; grade?: string; tags?: string[] }) =>
        api.post<ClassRow>(`${o}/classes`, body),
      get: (id: string) => api.get<ClassRow>(`${o}/classes/${id}`),
      update: (id: string, body: { name?: string; grade?: string; tags?: string[] }) =>
        api.patch<ClassRow>(`${o}/classes/${id}`, body),
      setStatus: (id: string, status: 'active' | 'archived') =>
        api.patch<ClassRow>(`${o}/classes/${id}/status`, { status }),
      members: (id: string) =>
        api.get<Omit<StudentRow, 'classes' | 'guardians'>[]>(`${o}/classes/${id}/members`),
      addMembers: (id: string, ids: string[]) =>
        api.post<{ added: number }>(`${o}/classes/${id}/members`, { ids }),
      removeMember: (id: string, studentId: string) => api.delete(`${o}/classes/${id}/members/${studentId}`),
      addTeachers: (id: string, ids: string[]) =>
        api.post<{ added: number }>(`${o}/classes/${id}/teachers`, { ids }),
      removeTeacher: (id: string, accountId: string) =>
        api.delete(`${o}/classes/${id}/teachers/${accountId}`),
      precheck: (id: string, rows: ImportRow[]) =>
        api.post<{ ok: boolean; errors: ImportError[] }>(`${o}/classes/${id}/precheck`, { rows }),
      import: (id: string, rows: ImportRow[]) =>
        api.post<{ ok: boolean; errors: ImportError[]; created: number; merged: number; joined: number }>(
          `${o}/classes/${id}/import`,
          { rows },
        ),
      assignCourse: (id: string, body: { courseId: string; lessonHours?: number; expiresAt?: string }) =>
        api.post<{ courseIds: string[]; members: number; created: number }>(
          `${l}/classes/${id}/courses`,
          body,
        ),
      syncCourses: (id: string) =>
        api.post<{ courses: number; members: number; created: number }>(`${l}/classes/${id}/sync-courses`),
      progress: (id: string, courseId: string) =>
        api.get<ClassProgressRow[]>(`${l}/classes/${id}/progress`, { query: { courseId } }),
    },
    students: {
      list: (q: { page?: number; size?: number; q?: string; classId?: string }) =>
        api.get<Paged<StudentRow>>(`${o}/students`, { query: q }),
      get: (id: string) => api.get<StudentView>(`${o}/students/${id}`),
      invite: (id: string) =>
        api.post<{ code: string; expiresAt: string }>(`${o}/students/${id}/guardian-invite`),
      /** 重新采集人脸底片(下次检录覆盖) */
      resetFace: (id: string) => api.post<{ template: string }>(`${o}/students/${id}/face-template/reset`),
      ledger: (id: string) => api.get<LedgerRow[]>(`${o}/students/${id}/lesson-hours`),
      adjust: (id: string, body: { delta: number; reason: 'grant' | 'adjust' | 'refund'; note?: string }) =>
        api.post<{ balance: number; negative: boolean }>(`${o}/students/${id}/lesson-hours`, body),
      overview: (id: string) => api.get<CourseOverview[]>(`${l}/students/${id}/overview`),
      progress: (id: string) => api.get<ProgressRow[]>(`${l}/students/${id}/progress`),
      unlock: (id: string, levelId: string) => api.post(`${l}/students/${id}/levels/${levelId}/unlock`),
      /** 推送前置关卡(教师介入的关) */
      pushReview: (id: string, levelId: string) =>
        api.post<{ levels: number }>(`${l}/students/${id}/levels/${levelId}/push-review`),
      /** 教师介入 · 放回学习:开新一轮作答、补强次数清零 */
      resume: (id: string, levelId: string) =>
        api.post<{ status: string; attempt: number }>(`${l}/students/${id}/levels/${levelId}/resume`),
      /** 教师介入 · 判定通过(理由必填;不进榜,解锁下一关、扣首次通关课时) */
      pass: (id: string, levelId: string, reason: string) =>
        api.post<{ unlockedLevelIds: string[] }>(`${l}/students/${id}/levels/${levelId}/pass`, { reason }),
      /** 教师介入 · 解锁下一关(本关仍算未通过) */
      unlockNext: (id: string, levelId: string) =>
        api.post<{ levelIds: string[] }>(`${l}/students/${id}/levels/${levelId}/unlock-next`),
      /** 学员的阶段测评(在用的 + 故障重考作废的) */
      assessments: (id: string) =>
        api.get<{ items: StudentAssessment[]; voided: StudentAssessment[] }>(
          `${l}/students/${id}/assessments`,
        ),
      /** 重置测评(故障重考,理由必填) */
      resetAssessment: (id: string, attemptId: string, reason: string) =>
        api.post<{ ok: boolean }>(`${l}/students/${id}/assessments/${attemptId}/reset`, { reason }),
      /** 回收课程权限(理由必填;进度保留,已开始的关只读) */
      revokeEntitlement: (entitlementId: string, reason: string) =>
        api.post<{ ok: boolean }>(`${l}/entitlements/${entitlementId}/revoke`, { reason }),
    },
    ledger: (page = 1, size = 30) =>
      api.get<Paged<LedgerRow>>(`${o}/lesson-hours`, { query: { page, size } }),
    review: {
      queue: () => api.get<ReviewItem[]>(`${l}/review-queue`),
      /** 出分;adoptAi = 一键采用 AI 建议分(仍算教师确认);挂了评分表的作品传 sheet(维度键 → 分)不传 score */
      grade: (
        id: string,
        body: { score?: number; feedback?: string; adoptAi?: boolean; sheet?: Record<string, number> },
      ) => api.post(`${l}/submissions/${id}/grade`, body),
      giveBack: (id: string, reason: string) => api.post(`${l}/submissions/${id}/return`, { reason }),
      /** 生成 AI 建议分(配了 AI 服务才有) */
      aiSuggest: (id: string) =>
        api.post<{ score: number; comment: string; dims?: Record<string, number> }>(
          `${l}/submissions/${id}/ai-suggest`,
        ),
    },
    /** 批改与预警 › 数字人待答(超出本关的问题):回复 / 无需回复 */
    dhQuestions: {
      list: (status: 'open' | 'done' = 'open') =>
        api.get<DhQuestion[]>('/v1/admin/dh/questions', { query: { status } }),
      reply: (id: string, reply: string) =>
        api.post<{ ok: boolean }>(`/v1/admin/dh/questions/${id}/reply`, { reply }),
      dismiss: (id: string) => api.post<{ ok: boolean }>(`/v1/admin/dh/questions/${id}/dismiss`),
    },
    alerts: {
      list: (status: 'open' | 'done' = 'open') => api.get<AlertRow[]>(`${l}/alerts`, { query: { status } }),
      done: (id: string) => api.post(`${l}/alerts/${id}/done`),
      scan: () => api.post<{ inactive: number; overdue: number }>(`${l}/alerts/scan`),
    },
    courses: () => api.get<CourseLite[]>('/v1/content/courses'),
    /** 课程详情(关卡清单,给逐关进度与手动解锁用) */
    course: (id: string) =>
      api.get<CourseLite & { levels: { id: string; seq: number; name: string; status: string }[] }>(
        `/v1/content/courses/${id}`,
      ),
  };
}
export type TeachingApi = ReturnType<typeof teachingApi>;
